import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Package,
  MapPin,
  Plus,
  Settings,
  LogOut,
  Handshake
} from 'lucide-react';
import { Logo } from './Logo';
import { FirebaseStatus } from './FirebaseStatus';
import { GeolocationStatus } from './GeolocationStatus';
import { useAuthStore } from '../../store/authStore';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import toast from 'react-hot-toast';

interface SidebarProps {
  className?: string;
  onClose?: () => void;
}

const navigation = [
  { name: 'Ruta de Hoy', href: '/', icon: MapPin, color: 'text-cyan-400' },
  { name: 'Clientes', href: '/clientes', icon: Users, color: 'text-blue-400' },
  { name: 'Entregas', href: '/entregas', icon: Plus, color: 'text-green-400' },
  { name: 'Inventario', href: '/inventario', icon: Package, color: 'text-violet-400' },
  { name: 'Comodato', href: '/comodato', icon: Handshake, color: 'text-amber-400' },
  { name: 'Resumen', href: '/resumen', icon: Home, color: 'text-indigo-400' },
];

const secondaryNav = [
  { name: 'Ajustes', href: '/ajustes', icon: Settings, color: 'text-gray-400' },
];

export const Sidebar: React.FC<SidebarProps> = ({ className = '', onClose }) => {
  const { setUser, setUserData } = useAuthStore();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserData(null);
      toast.success('Sesión cerrada correctamente');
    } catch {
      toast.error('Error al cerrar sesión');
    }
  };

  const handleLinkClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const navItemClass = (isActive: boolean) =>
    `group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative overflow-hidden ${
      isActive
        ? 'sidebar-item-active'
        : `text-gray-400 hover:text-white hover:bg-white/5 dark:hover:bg-white/5`
    }`;

  return (
    <aside
      className={`
        w-64 flex flex-col
        bg-primary-900
        border-r border-primary-900/40
        shadow-sm
        ${className}
      `}
    >
      {/* Logo */}
      <div className="flex-none px-6 pt-6 pb-4">
        <Logo size="2xl" />
        {/* Línea decorativa plana */}
        <div className="mt-4 h-px bg-primary-900/40" />
      </div>

      {/* Navegación principal */}
      <nav className="flex-1 px-3 py-2 overflow-y-auto">
        <p className="px-4 mb-2 text-xs font-semibold text-primary-400/60 uppercase tracking-widest">
          Módulos
        </p>
        <ul className="space-y-0.5">
          {navigation.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.href}
                end={item.href === '/'}
                onClick={handleLinkClick}
                className={({ isActive }) => navItemClass(isActive)}
              >
                {({ isActive }) => (
                  <>
                    {/* Hover state flat */}
                    <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/5 rounded-xl" />
                    {/* Ícono */}
                    <span
                      className={`
                        flex-none p-1.5 rounded-lg transition-all duration-200
                        ${isActive
                          ? 'bg-primary-500/20 text-primary-200'
                          : `bg-white/5 ${item.color} group-hover:bg-white/10 group-hover:text-primary-200`
                        }
                      `}
                    >
                      <item.icon className="h-4 w-4" />
                    </span>
                    <span className={isActive ? 'text-primary-200 font-semibold' : 'text-primary-300/80 group-hover:text-primary-200'}>
                      {item.name}
                    </span>
                    {/* Indicador activo plano */}
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-400" />
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Separador */}
        <div className="my-4 h-px bg-primary-900/40 mx-4" />

        {/* Navegación secundaria */}
        <ul className="space-y-0.5">
          {secondaryNav.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.href}
                onClick={handleLinkClick}
                className={({ isActive }) => navItemClass(isActive)}
              >
                {({ isActive }) => (
                  <>
                    <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/5 rounded-xl" />
                    <span
                      className={`
                        flex-none p-1.5 rounded-lg transition-all duration-200
                        ${isActive
                          ? 'bg-primary-500/20 text-primary-200'
                          : `bg-white/5 ${item.color} group-hover:bg-white/10`
                        }
                      `}
                    >
                      <item.icon className="h-4 w-4" />
                    </span>
                    <span className={isActive ? 'text-primary-200 font-semibold' : 'text-primary-300/80 group-hover:text-primary-200'}>
                      {item.name}
                    </span>
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-400" />
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer del sidebar */}
      <div className="flex-none px-3 pb-4 space-y-2">
        {/* Separador */}
        <div className="h-px bg-primary-900/40 mx-2 mb-3" />

        {/* Estado de Firebase */}
        <div className="px-1 lg:block hidden">
          <FirebaseStatus />
        </div>

        {/* Estado de Geolocalización */}
        <div className="px-1">
          <GeolocationStatus />
        </div>

        {/* Cerrar sesión */}
        <button
          onClick={handleLogout}
          className="w-full group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-primary-400/80 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
        >
          <span className="flex-none p-1.5 rounded-lg bg-white/5 text-primary-400/80 group-hover:bg-red-500/20 group-hover:text-red-400 transition-all duration-200">
            <LogOut className="h-4 w-4" />
          </span>
          <span>Cerrar Sesión</span>
        </button>
      </div>
    </aside>
  );
};